import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SettingsEditableValue } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Label,
  Stack,
  useToast,
} from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { updateUserProfileMutation, userFragment } from './operations';

const userFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type UserFormValues = z.infer<typeof userFormSchema>;

const EditProfileDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const toast = useToast();
  const userId = useUserIdFromSession();

  const userData = useCompleteFragment({
    fragment: userFragment,
    key: { id: userId },
  });

  const { control, formState, handleSubmit, reset } = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: userData?.firstName ?? '',
      lastName: userData?.lastName ?? '',
    },
  });

  const [updateProfile] = useMutation(updateUserProfileMutation, {
    onError: () => toast.error('Failed to update profile'),
    optimisticResponse: (data) => ({
      updateUsersByPk: {
        __typename: 'Users' as const,
        id: userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: userData?.email ?? '',
      },
    }),
  });

  useEffect(() => {
    if (open) {
      reset({
        firstName: userData?.firstName ?? '',
        lastName: userData?.lastName ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = handleSubmit((data) => {
    onOpenChange(false);

    return updateProfile({
      variables: {
        userId,
        firstName: data.firstName,
        lastName: data.lastName,
      },
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Edit Full Name</DialogTitle>
        </DialogHeader>
        <Stack gap={4} align="stretch">
          <Label>
            <Stack gap={2}>
              First Name
              <FormField
                control={control}
                name="firstName"
                render={({ field }) => (
                  <Input placeholder="First Name" {...field} />
                )}
              />
            </Stack>
          </Label>
          <Label>
            <Stack gap={2}>
              Last Name
              <FormField
                control={control}
                name="lastName"
                render={({ field }) => (
                  <Input placeholder="Last Name" {...field} />
                )}
              />
            </Stack>
          </Label>
        </Stack>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onSubmit}
            disabled={
              formState.isSubmitting || !formState.isValid || !formState.isDirty
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const UserProfileSettings = () => {
  const userId = useUserIdFromSession();

  const userData = useCompleteFragment({
    fragment: userFragment,
    key: { id: userId },
  });

  const fullName = [userData?.firstName, userData?.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <SettingsEditableValue
      value={fullName}
      editLabel="Edit full name"
      renderDialog={(props) => <EditProfileDialog {...props} />}
    />
  );
};
